/**
 * RVF WASM Store — Vector search engine using the .rvf cognitive container.
 *
 * Loads the pre-built drugs.rvf file (with HNSW index + witness chain)
 * via @ruvector/rvf-wasm's low-level C-ABI. Provides high-level
 * create/open/query/verify methods.
 *
 * This handles VECTOR SEARCH only. For SQL/Cypher structured queries,
 * use the RvLite engine in db.ts.
 *
 * Architecture:
 *   rvf-wasm (46KB) → loads drugs.rvf → HNSW vector search
 *   rvlite (865KB)  → SQL/Cypher on drug metadata, patient data, audit
 */

import initRvfWasm from '@ruvector/rvf-wasm';
import type { RvfWasmExports } from '@ruvector/rvf-wasm';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let wasm: RvfWasmExports | null = null;
let storeHandle: number = 0;
let initPromise: Promise<void> | null = null;

const VECTOR_DIM = 384;
const METRIC_COSINE = 1; // 0=L2, 1=cosine, 2=inner product

// ---------------------------------------------------------------------------
// WASM memory helpers
// ---------------------------------------------------------------------------

function writeFloat32Array(data: Float32Array): number {
  if (!wasm) throw new Error('RVF WASM not initialized');
  const byteLen = data.byteLength;
  const ptr = wasm.rvf_alloc(byteLen);
  const view = new Float32Array(wasm.memory.buffer, ptr, data.length);
  view.set(data);
  return ptr;
}

function readFloat32Array(ptr: number, len: number): Float32Array {
  if (!wasm) throw new Error('RVF WASM not initialized');
  return new Float32Array(wasm.memory.buffer, ptr, len).slice();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize the RVF WASM microkernel and load the drugs.rvf file.
 * Called once at app startup. Subsequent calls are no-ops.
 */
export async function initRvfStore(): Promise<void> {
  if (storeHandle > 0) return;
  if (initPromise) return initPromise;

  initPromise = _doInit();
  return initPromise;
}

async function _doInit(): Promise<void> {
  // 1. Initialize the WASM module (~46KB).
  wasm = await initRvfWasm();

  // 2. Try to load the pre-built .rvf file.
  try {
    const resp = await fetch('/drugs.rvf');
    if (!resp.ok) throw new Error(`drugs.rvf: HTTP ${resp.status}`);

    const rvfBytes = new Uint8Array(await resp.arrayBuffer());
    console.log(`[AfterCare] Loaded drugs.rvf: ${rvfBytes.byteLength} bytes`);

    // Write the .rvf bytes into WASM memory and open as a store.
    const ptr = wasm.rvf_alloc(rvfBytes.byteLength);
    const wasmView = new Uint8Array(wasm.memory.buffer, ptr, rvfBytes.byteLength);
    wasmView.set(rvfBytes);

    storeHandle = wasm.rvf_store_open(ptr, rvfBytes.byteLength);
    wasm.rvf_free(ptr, rvfBytes.byteLength);

    if (storeHandle <= 0) {
      console.warn('[AfterCare] rvf_store_open returned invalid handle, creating empty store');
      storeHandle = wasm.rvf_store_create(VECTOR_DIM, METRIC_COSINE);
    }

    const count = wasm.rvf_store_count(storeHandle);
    console.log(`[AfterCare] RVF store opened: ${count} vectors, handle=${storeHandle}`);
  } catch (err) {
    console.warn('[AfterCare] Could not load drugs.rvf, creating empty store:', err);
    storeHandle = wasm.rvf_store_create(VECTOR_DIM, METRIC_COSINE);
  }
}

/**
 * Query the RVF HNSW index for nearest neighbors.
 *
 * @param queryVector - 384-dim Float32Array (MiniLM-L6-v2 embedding)
 * @param k - Number of results to return
 * @returns Array of { id: number, distance: number }
 */
export function queryRvf(
  queryVector: Float32Array,
  k: number = 10,
): { id: number; distance: number }[] {
  if (!wasm || storeHandle <= 0) return [];

  // Write query vector to WASM memory.
  const queryPtr = writeFloat32Array(queryVector);

  // Allocate output buffer: k * (8 bytes id + 4 bytes distance) = k * 12.
  const outSize = k * 12;
  const outPtr = wasm.rvf_alloc(outSize);

  const resultCount = wasm.rvf_store_query(
    storeHandle,
    queryPtr,
    k,
    METRIC_COSINE,
    outPtr,
  );

  wasm.rvf_free(queryPtr, queryVector.byteLength);

  if (resultCount <= 0) {
    wasm.rvf_free(outPtr, outSize);
    return [];
  }

  // Read results: each result is { id: BigInt64, distance: Float32 }.
  const results: { id: number; distance: number }[] = [];
  const view = new DataView(wasm.memory.buffer, outPtr, resultCount * 12);

  for (let i = 0; i < resultCount; i++) {
    const offset = i * 12;
    // Read 8-byte id as BigInt, convert to number (safe for our IDs).
    const idBig = view.getBigInt64(offset, true);
    const distance = view.getFloat32(offset + 8, true);
    results.push({ id: Number(idBig), distance });
  }

  wasm.rvf_free(outPtr, outSize);
  return results;
}

/**
 * Get the number of vectors in the RVF store.
 */
export function getRvfVectorCount(): number {
  if (!wasm || storeHandle <= 0) return 0;
  return wasm.rvf_store_count(storeHandle);
}

/**
 * Verify the RVF witness chain (HIPAA audit integrity).
 * Returns true if the chain is valid and untampered.
 */
export function verifyWitnessChain(rvfBytes: Uint8Array): boolean {
  if (!wasm) return false;

  const ptr = wasm.rvf_alloc(rvfBytes.byteLength);
  const view = new Uint8Array(wasm.memory.buffer, ptr, rvfBytes.byteLength);
  view.set(rvfBytes);

  const result = wasm.rvf_witness_verify(ptr, rvfBytes.byteLength);
  wasm.rvf_free(ptr, rvfBytes.byteLength);

  return result === 0; // 0 = valid
}

/**
 * Check if the RVF store is ready for queries.
 */
export function isRvfReady(): boolean {
  return wasm !== null && storeHandle > 0;
}
